import React, { useEffect, useMemo, useState } from "react"

import type { IPagination } from "@common-shared/types/general.types"

import { BrowserStorageType } from "@front-shared/config/general.config"
import { BrowserStorageHelper } from "@front-shared/helpers/browserStorage.helper"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"

import { NETWORK_STATUS_CONFIG } from "../../../config/networkStatus.config"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useScrollableElementBoundaries } from "../../../hooks/scrollableElementBoundaries.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { networkStatusSelector } from "../../../store/selectors/networkStatus.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { networkStatusGetRulesList } from "../../../store/thunks/networkStatus.thunks"
import { PoliciesListPaginationComponent } from "../../policies/policiesList/policiesListPagination.component"
import { RulesTableComponent } from "./rulesTable.component"

export const RulesListComponent: React.FunctionComponent<
    IRulesListComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const networkStatusState = useAppSelector(networkStatusSelector)

    const scrollableTableBoundaries = useScrollableElementBoundaries(
        props.scrollableTableRef,
    )

    const [selectedPaginationOption, setSelectedPaginationOption] =
        useState<ISelectRenderedOption>(
            BrowserStorageHelper.get<ISelectRenderedOption>(
                NETWORK_STATUS_CONFIG.listPerPageStorageKey,
                undefined,
                BrowserStorageType.sessionStorage,
            ) ?? POLICIES_CONFIG.availablePaginationOptions[0],
        )

    const isListLoading = useMemo<boolean>(() => {
        return (
            props.isListLoading ||
            RuntimeHelper.isNetworkStatusRulesListLoading()
        )
    }, [runtimeState.isLoading, props.isListLoading])

    const missingDataMessage = useMemo<string | undefined>(() => {
        return isListLoading
            ? undefined
            : !props.selectedRulesVersion
              ? "No rules version selected"
              : !networkStatusState.items.length &&
                  !networkStatusState.pagination.total
                ? "No rules found"
                : undefined
    }, [
        props.selectedRulesVersion,
        isListLoading,
        networkStatusState.items.length,
        networkStatusState.pagination.total,
    ])

    const getItems = (pagination: IPagination) => {
        if (!selectedPracticeRole || !props.selectedRulesVersion) {
            return
        }

        return dispatch(
            networkStatusGetRulesList({
                payload: {
                    version: props.selectedRulesVersion,
                    pagination,
                    filters: networkStatusState.filters,
                },
                practice: selectedPracticeRole.practice,
            }),
        )
    }

    useEffect(() => {
        BrowserStorageHelper.set(
            NETWORK_STATUS_CONFIG.listPerPageStorageKey,
            selectedPaginationOption,
            BrowserStorageType.sessionStorage,
        )
    }, [selectedPaginationOption])

    // Get items on component load and when selected version or pagination options are changing
    useEffect(() => {
        const promise = getItems(
            UtilHelper.getPagination(selectedPaginationOption.value),
        )

        return () => {
            try {
                promise?.abort()
            } catch (_e) {}
        }
    }, [
        selectedPracticeRole?.id,
        props.selectedRulesVersion,
        selectedPaginationOption.value,
        JSON.stringify(networkStatusState.filters),
    ])

    return (
        <div
            data-produktly="network-status-results"
            className={`relative ${props.className}`}
        >
            <RulesTableComponent
                scrollableElementRef={props.scrollableTableRef}
                rules={networkStatusState.items}
                isListLoading={isListLoading}
                missingDataMessage={missingDataMessage}
            />

            {!missingDataMessage && !isListLoading ? (
                <PoliciesListPaginationComponent
                    className="md:px-[24px] px-[48px]"
                    style={{
                        right: scrollableTableBoundaries?.x,
                    }}
                    getNewItems={getItems}
                    nextPagination={networkStatusState.pagination}
                    selectedPaginationOption={selectedPaginationOption}
                    setSelectedPaginationOption={setSelectedPaginationOption}
                />
            ) : null}
        </div>
    )
}

interface IRulesListComponentProps {
    className?: string
    scrollableTableRef?: React.RefObject<HTMLDivElement>
    selectedRulesVersion: number | null
    isListLoading?: boolean
}
